import { Fragment, useMemo, useRef } from 'react';
import { Plus } from 'lucide-react';
import { useLiveQuery } from 'dexie-react-hooks';
import { EmptyState } from '../ui/EmptyState';
import { Button } from '../ui/Button';
import { useDragSort } from '../ui/useDragSort';
import { useFlip } from '../ui/useFlip';
import { SearchButton } from '../search/SearchButton';
import { listRooms, reorderRooms } from '../db/rooms';
import { iconOrdinals } from './iconOrdinals';
import { RoomCard } from './RoomCard';
import { useRoomDialogs } from './RoomDialogs';
import './Home.css';

/** Living Spaces, not Pinterest: wide margins, few elements, lots of air. */
export function Home() {
  const rooms = useLiveQuery(listRooms, []);
  const dialogs = useRoomDialogs();
  const grid = useRef<HTMLUListElement>(null);

  const ids = useMemo(() => (rooms ?? []).map((room) => room.id), [rooms]);

  const sortable = useDragSort({
    ids,
    onCommit: (ordered) => void reorderRooms(ordered),
  });

  // The cards sit in a plain CSS grid, so reordering them moves them with no
  // animation of its own. This is what makes them slide.
  useFlip(grid, sortable.order.join(), sortable.draggingId);

  const ordered = useMemo(() => {
    const byId = new Map((rooms ?? []).map((room) => [room.id, room]));
    return sortable.order.flatMap((id) => {
      const room = byId.get(id);
      return room === undefined ? [] : [room];
    });
  }, [rooms, sortable.order]);

  // Numbered against the arrangement you're looking at, so a drag renumbers
  // as it lands rather than a moment later.
  const ordinals = useMemo(() => iconOrdinals(ordered), [ordered]);

  // undefined means the first read hasn't landed. Rendering the empty state
  // in that gap would flash "No rooms yet" on every launch.
  if (rooms === undefined) {
    return <div className="home" aria-busy="true" />;
  }

  return (
    <div className="home">
      <div className="heading-row home__heading">
        <h1 className="display home__title">Your Rooms</h1>
        <SearchButton scope="everywhere" label="Search Everything" />
      </div>

      {rooms.length === 0 ? (
        <EmptyState
          title="No Rooms Yet"
          line="Start with the room you've bought the most for."
          action={<Button onClick={dialogs.openCreate}>Add Room</Button>}
        />
      ) : (
        <ul
          className={`home__grid${sortable.draggingId === undefined ? '' : ' sorting'}`}
          ref={grid}
        >
          {ordered.map((room) => (
            <Fragment key={room.id}>
              {/* A lifted card is fixed to the cursor, so the grid stops
                  giving it a cell and the others close over the space it
                  will drop into. This holds it open. */}
              {sortable.draggingId === room.id && sortable.size !== undefined && (
                <li
                  className="home__hole"
                  aria-hidden="true"
                  style={{ height: `${sortable.size.h}px` }}
                />
              )}
              <RoomCard
                room={room}
                ordinal={ordinals.get(room.id)}
                lifted={sortable.draggingId === room.id}
                sort={sortable.bind(room.id)}
              />
            </Fragment>
          ))}

          {/* Same size as a card, and it sits last. */}
          <li className="home__add">
            <button type="button" className="home__add-button" onClick={dialogs.openCreate}>
              <Plus size={28} aria-hidden />
              <span>Add Room</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
